import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
    DragIndicator as DragIcon,
    MergeType as MergeIcon,
    Remove as RemoveIcon,
    Refresh as ResetIcon,
    Save as SaveIcon,
    AccessTime as TimeIcon,
    Upload as UploadIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Input,
    InputAdornment,
    List,
    ListItem,
    Paper,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';

import Swal from 'sweetalert2';

const MergeSrtDialog = ({
    open,
    onClose,
    videoFiles = [],
    folderName = 'folder',
    currentPath = '/',
    userFirstName = 'User'
}) => {
    useTrackUsage('MergeSrtDialog', 'dialog', 'folder-browser', open);
    const [videoOrder, setVideoOrder] = useState([]);
    const [startOffset, setStartOffset] = useState(0);
    const [videoOffsets, setVideoOffsets] = useState({});
    const [processing, setProcessing] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [startOffsetError, setStartOffsetError] = useState('');
    const [videoOffsetErrors, setVideoOffsetErrors] = useState({});
    const [startOffsetInput, setStartOffsetInput] = useState('0');
    const [videoOffsetInputs, setVideoOffsetInputs] = useState({});
    const fileInputRef = useRef(null);

    // Parse duration from HH:MM:SS.xx format to milliseconds
    const parseDurationToMs = (durationStr) => {
        if (!durationStr || typeof durationStr !== 'string') {
            return Math.floor(Math.random() * 300000) + 60000; // Fallback to mock duration
        }
        
        try {
            const parts = durationStr.split(':');
            if (parts.length !== 3) {
                return Math.floor(Math.random() * 300000) + 60000; // Fallback to mock duration
            }
            
            const hours = parseInt(parts[0]) || 0;
            const minutes = parseInt(parts[1]) || 0;
            const secondsParts = parts[2].split('.');
            const seconds = parseInt(secondsParts[0]) || 0;
            const centiseconds = parseInt(secondsParts[1]) || 0;
            
            // Convert to milliseconds
            const totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000 + (centiseconds * 10);
            return totalMs > 0 ? totalMs : Math.floor(Math.random() * 300000) + 60000;
        } catch (error) {
            return Math.floor(Math.random() * 300000) + 60000; // Fallback to mock duration
        }
    };

    // Get all available languages from videos
    const availableLanguages = useMemo(() => {
        // Use current videoOrder (reflects deletions) instead of original videoFiles
        if (videoOrder.length === 0) return [];
        
        // Get languages for each video in current order
        const videoLanguages = videoOrder.map(video => {
            if (!video.languages || !Array.isArray(video.languages)) {
                return [];
            }
            return video.languages
                .filter(lang => lang.vttFileUrl)
                .map(lang => lang.language || lang.name || 'Unknown');
        });
        
        // Find intersection of all video languages (languages supported by ALL remaining videos)
        if (videoLanguages.length === 0) return [];
        
        // Start with first video's languages and find intersection
        const intersection = videoLanguages[0].filter(lang => {
            return videoLanguages.every(videoLangs => videoLangs.includes(lang));
        });
        
        return intersection.sort();
    }, [videoOrder]);

    // Convert milliseconds to HH:MM:SS.xxx format
    const msToTimeString = (ms) => {
        if (!ms || isNaN(ms) || ms < 0) {
            return "00:00:00.000";
        }
        
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const milliseconds = ms % 1000;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    };

    // Convert HH:MM:SS.xxx format to milliseconds
    const timeStringToMs = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') {
            return 0;
        }
        
        try {
            const parts = timeStr.split(':');
            if (parts.length !== 3) {
                return 0;
            }
            
            const hours = parseInt(parts[0]) || 0;
            const minutes = parseInt(parts[1]) || 0;
            const secondsParts = parts[2].split('.');
            const seconds = parseInt(secondsParts[0]) || 0;
            const milliseconds = parseInt(secondsParts[1]) || 0;
            
            return (hours * 3600 + minutes * 60 + seconds) * 1000 + milliseconds;
        } catch (error) {
            return 0;
        }
    };
    
    // Smart time input formatter - accepts various input formats
    const formatTimeInput = (input) => {
        // Remove any non-numeric characters except colons and dots
        let cleaned = input.replace(/[^0-9:.]/g, '');
        
        // If just numbers, try to auto-format
        if (!/[:.]/.test(cleaned)) {
            if (cleaned.length <= 3) {
                // Just milliseconds: "500" -> "00:00:00.500"
                return `00:00:00.${cleaned.padStart(3, '0')}`;
            } else if (cleaned.length <= 5) {
                // Seconds and milliseconds: "1500" -> "00:00:01.500"
                const ms = cleaned.slice(-3).padStart(3, '0');
                const sec = cleaned.slice(0, -3) || '0';
                return `00:00:${sec.padStart(2, '0')}.${ms}`;
            }
        }
        
        // If already has format, try to complete it
        const parts = cleaned.split(':');
        if (parts.length === 1) {
            // "5" -> "00:00:05.000"
            return `00:00:${parts[0].padStart(2, '0')}.000`;
        } else if (parts.length === 2) {
            // "1:30" -> "00:01:30.000"
            return `00:${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}.000`;
        }
        
        return cleaned;
    };
    
    // Validate time input format - only validates complete inputs
    const validateTimeInput = (input, isComplete = false) => {
        if (!input || input.trim() === '') {
            return { isValid: true, error: '' };
        }
        
        const trimmed = input.trim();
        
        // Allow partial inputs during typing (unless validating complete input)
        if (!isComplete) {
            // Allow partial patterns during typing
            const partialPatterns = [
                /^\d+$/, // Just numbers
                /^\d{1,2}:$/, // "1:"
                /^\d{1,2}:\d{0,2}$/, // "1:3" or "1:30"
                /^\d{1,2}:\d{1,2}:$/, // "1:30:"
                /^\d{1,2}:\d{1,2}:\d{0,2}$/, // "1:30:4" or "1:30:45"
                /^\d{1,2}:\d{1,2}:\d{1,2}\.$/, // "1:30:45."
                /^\d{1,2}:\d{1,2}:\d{1,2}\.\d{0,3}$/, // "1:30:45.5" or "1:30:45.500"
                /^\d{1,2}\.\d{0,3}$/ // "5." or "5.500"
            ];
            
            const isValidPartial = partialPatterns.some(pattern => pattern.test(trimmed));
            if (isValidPartial) {
                return { isValid: true, error: '' };
            }
        }
        
        // Check for valid complete patterns
        const validPatterns = [
            /^\d+$/, // Just numbers (milliseconds or seconds)
            /^\d{1,2}:\d{1,2}$/, // MM:SS
            /^\d{1,2}:\d{1,2}:\d{1,2}$/, // HH:MM:SS
            /^\d{1,2}:\d{1,2}:\d{1,2}\.\d{1,3}$/, // HH:MM:SS.mmm
            /^\d{1,2}\.\d{1,3}$/ // SS.mmm
        ];
        
        const isValidPattern = validPatterns.some(pattern => pattern.test(trimmed));
        
        if (!isValidPattern) {
            return { 
                isValid: false, 
                error: 'Invalid format. Use: 5 (seconds), 1:30 (min:sec), 1:30:45 (hour:min:sec), or 1:30:45.500 (with milliseconds)' 
            };
        }
        
        // Additional validation for ranges
        if (trimmed.includes(':')) {
            const parts = trimmed.split(':');
            for (let i = 0; i < parts.length; i++) {
                const part = parts[i].split('.')[0]; // Remove milliseconds for validation
                const num = parseInt(part);
                
                if (isNaN(num)) continue; // Skip empty parts
                
                if (i === 0) { // Hours
                    if (num < 0 || num > 23) {
                        return { isValid: false, error: 'Hours must be between 0-23' };
                    }
                } else { // Minutes and seconds
                    if (num < 0 || num > 59) {
                        return { isValid: false, error: 'Minutes and seconds must be between 0-59' };
                    }
                }
            }
        }
        
        return { isValid: true, error: '' };
    };
    
    // Handle smart offset input
    const handleSmartOffsetChange = (videoId, value) => {
        setVideoOffsetInputs(prev => ({ ...prev, [videoId]: value }));
        
        // Allow typing without validation, only validate on blur
        const validation = validateTimeInput(value, false);
        
        if (!validation.isValid) {
            setVideoOffsetErrors(prev => ({ ...prev, [videoId]: validation.error }));
            return;
        } else {
            // Clear error if validation passes
            setVideoOffsetErrors(prev => ({ ...prev, [videoId]: '' }));
        }
        
        if (value === '') {
            setVideoOffsets(prev => ({ ...prev, [videoId]: 0 }));
            return;
        }
        
        try {
            const formatted = formatTimeInput(value);
            const ms = timeStringToMs(formatted);
            setVideoOffsets(prev => ({ ...prev, [videoId]: Math.max(0, ms) }));
        } catch (error) {
            // Don't update offset if formatting fails, just allow typing
        }
    };
    
    // Handle blur validation for video offset
    const handleVideoOffsetBlur = (videoId, value) => {
        const validation = validateTimeInput(value, true);
        
        if (!validation.isValid) {
            setVideoOffsetErrors(prev => ({ ...prev, [videoId]: validation.error }));
        } else {
            setVideoOffsetErrors(prev => ({ ...prev, [videoId]: '' }));
            
            if (value !== '') {
                const formatted = formatTimeInput(value);
                const ms = timeStringToMs(formatted);
                setVideoOffsets(prev => ({ ...prev, [videoId]: Math.max(0, ms) }));
                // Update input to show formatted value
                setVideoOffsetInputs(prev => ({ ...prev, [videoId]: msToTimeString(ms) }));
            } else {
                setVideoOffsets(prev => ({ ...prev, [videoId]: 0 }));
                setVideoOffsetInputs(prev => ({ ...prev, [videoId]: '0' }));
            }
        }
    };
    
    // Increment/decrement offset by specified milliseconds
    const adjustOffset = (videoId, deltaMs) => {
        const currentMs = videoOffsets[videoId] || 0;
        const newMs = Math.max(0, currentMs + deltaMs);
        setVideoOffsets(prev => ({ ...prev, [videoId]: newMs }));
    };
    
    // Handle smart start offset change
    const handleSmartStartOffsetChange = (value) => {
        setStartOffsetInput(value);
        
        // Allow typing without validation, only validate on blur
        const validation = validateTimeInput(value, false);
        
        if (!validation.isValid) {
            setStartOffsetError(validation.error);
            return;
        } else {
            // Clear error if validation passes
            setStartOffsetError('');
        }
        
        if (value === '') {
            setStartOffset(0);
            return;
        }
        
        try {
            const formatted = formatTimeInput(value);
            const ms = timeStringToMs(formatted);
            setStartOffset(Math.max(0, ms));
        } catch (error) {
            // Don't update offset if formatting fails, just allow typing
        }
    };
    
    // Handle blur validation for start offset
    const handleStartOffsetBlur = (value) => {
        const validation = validateTimeInput(value, true);
        
        if (!validation.isValid) {
            setStartOffsetError(validation.error);
        } else {
            setStartOffsetError('');
            
            if (value !== '') {
                const formatted = formatTimeInput(value);
                const ms = timeStringToMs(formatted);
                setStartOffset(Math.max(0, ms));
                // Update input to show formatted value
                setStartOffsetInput(msToTimeString(ms));
            } else {
                setStartOffset(0);
                setStartOffsetInput('0');
            }
        }
    };

    // Initialize video order and parse durations when dialog opens
    useEffect(() => {
        if (open && videoFiles.length > 0) {
            // Filter out videos with zero duration
            const validVideos = videoFiles.filter(video => {
                const duration = parseDurationToMs(video.duration);
                return duration > 0;
            });
            
            const videosWithData = validVideos.map((video, index) => ({
                id: video.data || video._id || `video_${index}`,
                msDuration: parseDurationToMs(video.duration),
                originalIndex: index,
                ...video
            }));
            setVideoOrder(videosWithData);
            
            // Reset offsets
            setStartOffset(0);
            setStartOffsetError('');
            setStartOffsetInput('0');
            setVideoOffsetErrors({});
            setVideoOffsetInputs({});
            const initialOffsets = {};
            const initialInputs = {};
            videosWithData.forEach(video => {
                initialOffsets[video.id] = 0;
                initialInputs[video.id] = '0';
            });
            setVideoOffsets(initialOffsets);
            setVideoOffsetInputs(initialInputs);
        }
    }, [open, videoFiles]);

    // Update selected languages when available languages change (due to deletions)
    useEffect(() => {
        if (availableLanguages.length > 0) {
            // Filter out any previously selected languages that are no longer available
            const validSelectedLanguages = selectedLanguages.filter(lang => 
                availableLanguages.includes(lang)
            );
            
            // If no valid languages remain, select the first available language
            if (validSelectedLanguages.length === 0) {
                setSelectedLanguages([availableLanguages[0]]);
            } else {
                // Update to only include valid languages
                setSelectedLanguages(validSelectedLanguages);
            }
        } else {
            // No common languages available
            setSelectedLanguages([]);
        }
    }, [availableLanguages]);

    // Calculate cumulative times and total duration
    const calculateTimeline = useCallback(() => {
        let currentTime = startOffset;
        const timeline = [];
        
        videoOrder.forEach((video, index) => {
            const videoOffset = videoOffsets[video.id] || 0;
            const startTime = currentTime + (index > 0 ? videoOffset : 0);
            const endTime = startTime + video.msDuration;
            
            timeline.push({
                ...video,
                startTime,
                endTime,
                offset: index > 0 ? videoOffset : 0
            });
            
            currentTime = endTime + (index > 0 ? videoOffset : 0);
        });
        
        return timeline;
    }, [videoOrder, startOffset, videoOffsets]);

    const timeline = calculateTimeline();
    const totalDuration = timeline.length > 0 ? timeline[timeline.length - 1].endTime : 0;

    // Handle drag and drop reordering
    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(videoOrder);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setVideoOrder(items);
    };

    // Handle offset changes
    const handleOffsetChange = (videoId, timeStr) => {
        const ms = timeStringToMs(timeStr);
        setVideoOffsets(prev => ({
            ...prev,
            [videoId]: Math.max(0, ms)
        }));
    };

    // Format duration to HH:MM:SS format
    const formatDuration = (ms) => {
        if (!ms || isNaN(ms) || ms < 0) {
            return "00:00:00";
        }
        
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Format time with centiseconds in HH:MM:SS.xx format
    const formatTimeWithMs = (ms) => {
        if (!ms || isNaN(ms) || ms < 0) {
            return "00:00:00.00";
        }
        
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const centiseconds = Math.floor((ms % 1000) / 10);
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`;
    };

    // Parse VTT file content
    const parseVttContent = (vttContent) => {
        const lines = vttContent.trim().split('\n');
        const subtitles = [];
        let currentSubtitle = {};
        let lineIndex = 0;
        
        // Skip WEBVTT header
        while (lineIndex < lines.length && !lines[lineIndex].includes('-->')) {
            lineIndex++;
        }
        
        while (lineIndex < lines.length) {
            const line = lines[lineIndex].trim();
            
            // Skip empty lines
            if (!line) {
                lineIndex++;
                continue;
            }
            
            // Check if it's a timestamp line
            if (/\d{2}:\d{2}:\d{2}\.\d{3} --> \d{2}:\d{2}:\d{2}\.\d{3}/.test(line)) {
                // Save previous subtitle if it exists
                if (currentSubtitle.text) {
                    subtitles.push(currentSubtitle);
                }
                
                const [startTime, endTime] = line.split(' --> ');
                currentSubtitle = {
                    number: subtitles.length + 1,
                    startTime: startTime,
                    endTime: endTime,
                    text: []
                };
                lineIndex++;
                continue;
            }
            
            // Otherwise, it's subtitle text
            if (currentSubtitle.startTime) {
                currentSubtitle.text.push(line);
            }
            lineIndex++;
        }
        
        // Add the last subtitle
        if (currentSubtitle.text && currentSubtitle.text.length > 0) {
            subtitles.push(currentSubtitle);
        }
        
        return subtitles;
    };

    // Parse SRT file content
    const parseSrtContent = (srtContent) => {
        const lines = srtContent.trim().split('\n');
        const subtitles = [];
        let currentSubtitle = {};
        let lineIndex = 0;

        while (lineIndex < lines.length) {
            const line = lines[lineIndex].trim();
            
            // Skip empty lines
            if (!line) {
                lineIndex++;
                continue;
            }

            // Check if it's a subtitle number
            if (/^\d+$/.test(line)) {
                // Save previous subtitle if it exists
                if (currentSubtitle.text) {
                    subtitles.push(currentSubtitle);
                }
                currentSubtitle = { number: parseInt(line) };
                lineIndex++;
                continue;
            }

            // Check if it's a timestamp line
            if (/\d{2}:\d{2}:\d{2},\d{3} --> \d{2}:\d{2}:\d{2},\d{3}/.test(line)) {
                const [startTime, endTime] = line.split(' --> ');
                currentSubtitle.startTime = startTime;
                currentSubtitle.endTime = endTime;
                currentSubtitle.text = [];
                lineIndex++;
                continue;
            }

            // Otherwise, it's subtitle text
            if (currentSubtitle.text !== undefined) {
                currentSubtitle.text.push(line);
            }
            lineIndex++;
        }

        // Add the last subtitle
        if (currentSubtitle.text) {
            subtitles.push(currentSubtitle);
        }

        return subtitles;
    };

    // Convert time format to milliseconds (handles both VTT and SRT)
    const timeToMs = (timeStr) => {
        if (timeStr.includes('.')) {
            // VTT format: HH:MM:SS.mmm
            const [time, ms] = timeStr.split('.');
            const [hours, minutes, seconds] = time.split(':').map(Number);
            return (hours * 3600 + minutes * 60 + seconds) * 1000 + parseInt(ms);
        } else {
            // SRT format: HH:MM:SS,mmm
            return srtTimeToMs(timeStr);
        }
    };

    // Convert SRT time format to milliseconds
    const srtTimeToMs = (timeStr) => {
        const [time, ms] = timeStr.split(',');
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return (hours * 3600 + minutes * 60 + seconds) * 1000 + parseInt(ms);
    };

    // Convert milliseconds to SRT time format
    const msToSrtTime = (ms) => {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const milliseconds = ms % 1000;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
    };

    // Adjust subtitle timestamps based on offset (works for both VTT and SRT)
    const adjustSubtitleTimestamps = (subtitles, offsetMs) => {
        return subtitles.map(subtitle => ({
            ...subtitle,
            startTime: msToSrtTime(timeToMs(subtitle.startTime) + offsetMs),
            endTime: msToSrtTime(timeToMs(subtitle.endTime) + offsetMs)
        }));
    };

    // Fetch VTT file content from video language data
    const fetchVttContent = async (video, language) => {
        try {
            if (!video.languages || !Array.isArray(video.languages)) {
                throw new Error(`No language data available for ${video.videoName}`);
            }
            
            const languageData = video.languages.find(lang => 
                (lang.language || lang.name) === language
            );
            
            if (!languageData || !languageData.vttFileUrl) {
                throw new Error(`No VTT file URL found for language "${language}" in video "${video.videoName}"`);
            }
            
            const response = await fetch(languageData.vttFileUrl);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch VTT file: ${response.status} ${response.statusText}`);
            }
            
            return await response.text();
        } catch (error) {
            console.error(`Error fetching VTT for ${video.videoName} (${language}):`, error);
            throw error;
        }
    };

    // Generate merged SRT content
    const generateMergedSrt = (allSubtitles) => {
        let srtContent = '';
        let subtitleNumber = 1;
        
        allSubtitles.forEach(subtitle => {
            srtContent += `${subtitleNumber}\n`;
            srtContent += `${subtitle.startTime} --> ${subtitle.endTime}\n`;
            
            // Remove NOTE / BOOKMARK lines that may be present in parsed subtitle text
            const lines = Array.isArray(subtitle.text) ? subtitle.text : [subtitle.text || ''];
            const filtered = lines.filter(line => {
                if (!line || !line.trim()) return false;
                return !/^\s*(NOTE\b|BOOKMARK\b)/i.test(line);
            });
            
            srtContent += `${filtered.join('\n')}\n\n`;
            subtitleNumber++;
        });
        
        return srtContent.trim();
    };

    // Save configuration to JSON file
    const handleSaveConfiguration = () => {
        const configuration = {
            folderName,
            videoOrder: videoOrder.map(video => ({
                id: video.id,
                videoName: video.videoName,
                msDuration: video.msDuration,
                originalIndex: video.originalIndex
            })),
            startOffset,
            videoOffsets,
            totalDuration,
            savedAt: new Date().toISOString()
        };
        
        const jsonData = JSON.stringify(configuration, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        // Format current date and time
        const now = new Date();
        const dateTimeString = now.toISOString()
            .replace(/[-:]/g, '')
            .replace(/\.\d{3}Z$/, '')
            .replace('T', '_');
        
        // Clean folder name and user name for filename
        const cleanFolderName = folderName.replace(/[^a-zA-Z0-9_-]/g, '_');
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `${cleanFolderName}_merge_config_${dateTimeString}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    
    // Load configuration from JSON file
    const handleLoadConfiguration = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const configuration = JSON.parse(e.target.result);
                
                // Validate configuration structure
                if (configuration.videoOrder && configuration.startOffset !== undefined && configuration.videoOffsets) {
                    // Check if all videos from configuration exist in current videoFiles
                    const configVideoIds = configuration.videoOrder.map(v => v.videoName);
                    const currentVideoNames = videoFiles.map(v => v.videoName );
                    
                    const missingVideos = configVideoIds.filter(name => !currentVideoNames.includes(name));
                    if (missingVideos.length > 0) {
                        await Swal.fire({
                            icon: 'warning',
                            title: 'Configuration Mismatch',
                            html: `<p><strong>Configuration contains videos not found in current selection:</strong></p><br><p>${missingVideos.join(', ')}</p>`,
                            confirmButtonColor: '#ff9800'
                        });
                        return;
                    }
                    
                    // Apply configuration
                    const reorderedVideos = [];
                    const usedIndices = new Set();
                    
                    // First, add videos in the saved order
                    configuration.videoOrder.forEach(savedVideo => {
                        const matchingVideoIndex = videoFiles.findIndex(v => 
                            (v.videoName) === savedVideo.videoName
                        );
                        if (matchingVideoIndex !== -1 && !usedIndices.has(matchingVideoIndex)) {
                            const video = videoFiles[matchingVideoIndex];
                            reorderedVideos.push({
                                id: video.data || video._id || `video_${matchingVideoIndex}`,
                                msDuration: parseDurationToMs(video.duration),
                                originalIndex: matchingVideoIndex,
                                ...video
                            });
                            usedIndices.add(matchingVideoIndex);
                        }
                    });
                    
                    // Add any remaining videos not in the configuration
                    videoFiles.forEach((video, index) => {
                        if (!usedIndices.has(index)) {
                            reorderedVideos.push({
                                id: video.data || video._id || `video_${index}`,
                                msDuration: parseDurationToMs(video.duration),
                                originalIndex: index,
                                ...video
                            });
                        }
                    });
                    
                    setVideoOrder(reorderedVideos);
                    setStartOffset(configuration.startOffset);
                    
                    // Map old video offsets to new video IDs
                    const newOffsets = {};
                    reorderedVideos.forEach(video => {
                        const savedVideo = configuration.videoOrder.find(v => v.videoName === video.videoName);
                        if (savedVideo && configuration.videoOffsets[savedVideo.id] !== undefined) {
                            newOffsets[video.id] = configuration.videoOffsets[savedVideo.id];
                        } else {
                            newOffsets[video.id] = 0;
                        }
                    });
                    setVideoOffsets(newOffsets);
                    
                    console.log('Configuration loaded successfully:', configuration);
                } else {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Invalid Configuration',
                        text: 'The configuration file format is invalid.',
                        confirmButtonColor: '#f44336'
                    });
                }
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Configuration Load Error',
                    text: 'Error reading configuration file: ' + error.message,
                    confirmButtonColor: '#f44336'
                });
                console.error('Error loading configuration:', error);
            }
        };
        reader.readAsText(file);
        
        // Reset file input
        event.target.value = '';
    };
    
    // Trigger file input click
    const handleLoadClick = () => {
        fileInputRef.current?.click();
    };

    // Handle merge execution
    const handleMerge = async () => {
        setProcessing(true);
        
        // Close the dialog immediately
        onClose();
        
        // Show loading dialog
        Swal.fire({
            title: 'Merging Subtitle Files',
            html: `
                <div style="text-align: left; margin: 20px 0;">
                    <p><strong>Processing ${selectedLanguages.length} language(s) from ${videoOrder.length} videos...</strong></p>
                    <div style="margin: 15px 0;">
                        <div style="background: #f5f5f5; padding: 10px; border-radius: 5px; font-family: monospace; font-size: 12px; text-align: left;">
                            <div id="progress-log">Starting merge process...</div>
                        </div>
                    </div>
                </div>
            `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: true,
            confirmButtonText: 'Close',
            
        });
        
        // Helper function to update progress
        const updateProgress = (message) => {
            const progressLog = document.getElementById('progress-log');
            if (progressLog) {
                progressLog.innerHTML += `<br>• ${message}`;
                progressLog.scrollTop = progressLog.scrollHeight;
            }
        };
        
        try {
            console.log('Starting SRT merge process...');
            console.log('Selected languages:', selectedLanguages);
            console.log('Video order:', videoOrder.map(v => ({ videoName: v.videoName, languages: v.languages?.map(l => l.language || l.name) })));
            
            if (selectedLanguages.length === 0) {
                Swal.close();
                await Swal.fire({
                    icon: 'warning',
                    title: 'No Languages Selected',
                    text: 'Please select at least one language to merge.',
                    confirmButtonColor: '#1976d2'
                });
                setProcessing(false);
                return;
            }
            
            if (videoOrder.length < 2) {
                Swal.close();
                await Swal.fire({
                    icon: 'info',
                    title: 'Not Enough Videos',
                    text: 'Please select at least 2 videos to merge.',
                    confirmButtonColor: '#1976d2'
                });
                setProcessing(false);
                return;
            }
            
            let successfulMerges = 0;
            let totalErrors = 0;
            
            // Process each selected language
            for (const language of selectedLanguages) {
                console.log(`\n=== Processing language: ${language} ===`);
                updateProgress(`Processing ${language}...`);
                
                let allSubtitles = [];
                let currentTimeOffset = startOffset;
                let videoErrors = [];
                
                // Process each video in order
                for (let videoIndex = 0; videoIndex < videoOrder.length; videoIndex++) {
                    const video = videoOrder[videoIndex];
                    const videoOffset = videoOffsets[video.id] || 0;
                    const videoStartTime = currentTimeOffset + (videoIndex > 0 ? videoOffset : 0);
                    
                    try {
                        updateProgress(`Fetching subtitles for ${video.videoName}...`);
                        console.log(`Fetching VTT for ${video.videoName} (${language})`);
                        console.log(`Video ${videoIndex + 1}: Start time: ${formatTimeWithMs(videoStartTime)}, Duration: ${formatTimeWithMs(video.msDuration)}`);
                        
                        // Fetch VTT content for this video and language
                        const vttContent = await fetchVttContent(video, language);
                        console.log(`VTT content length: ${vttContent.length} characters`);
                        
                        // Parse VTT content
                        const subtitles = parseVttContent(vttContent);
                        console.log(`Parsed ${subtitles.length} subtitles from ${video.videoName}`);
                        updateProgress(`Found ${subtitles.length} subtitles in ${video.videoName}`);
                        
                        if (subtitles.length > 0) {
                            console.log(`First subtitle: ${subtitles[0].startTime} - ${subtitles[0].endTime}: ${subtitles[0].text?.join(' ') || 'No text'}`);
                            console.log(`Last subtitle: ${subtitles[subtitles.length - 1].startTime} - ${subtitles[subtitles.length - 1].endTime}: ${subtitles[subtitles.length - 1].text?.join(' ') || 'No text'}`);
                        }
                        
                        // Adjust timestamps based on video position in timeline
                        const adjustedSubtitles = adjustSubtitleTimestamps(subtitles, videoStartTime);
                        
                        if (adjustedSubtitles.length > 0) {
                            console.log(`After adjustment - First: ${adjustedSubtitles[0].startTime} - ${adjustedSubtitles[0].endTime}`);
                            console.log(`After adjustment - Last: ${adjustedSubtitles[adjustedSubtitles.length - 1].startTime} - ${adjustedSubtitles[adjustedSubtitles.length - 1].endTime}`);
                        }
                        
                        // Add adjusted subtitles to the collection
                        allSubtitles = allSubtitles.concat(adjustedSubtitles);
                        
                        // Update current time offset for next video
                        currentTimeOffset = videoStartTime + video.msDuration + (videoIndex > 0 ? videoOffset : 0);
                        
                    } catch (error) {
                        console.error(`Error processing ${video.videoName} for ${language}:`, error);
                        videoErrors.push(`${video.videoName}: ${error.message}`);
                        updateProgress(`⚠️ Error with ${video.videoName}: ${error.message}`);
                        totalErrors++;
                        continue; // Skip this video but continue with others
                    }
                }
                
                console.log(`Total subtitles collected for ${language}: ${allSubtitles.length}`);
                updateProgress(`Collected ${allSubtitles.length} total subtitles for ${language}`);
                
                if (allSubtitles.length === 0) {
                    console.warn(`No subtitles found for language: ${language}`);
                    if (videoErrors.length > 0) {
                        Swal.close();
                        await Swal.fire({
                            icon: 'error',
                            title: `No Subtitles Found - ${language}`,
                            html: `<strong>Errors encountered:</strong><br><br>${videoErrors.map(err => `• ${err}`).join('<br>')}`,
                            confirmButtonColor: '#1976d2'
                        });
                    } else {
                        Swal.close();
                        await Swal.fire({
                            icon: 'warning',
                            title: `No Subtitles Found - ${language}`,
                            html: `<strong>This might mean:</strong><br><br>• VTT files are empty<br>• VTT files have invalid format<br>• Network issues fetching files`,
                            confirmButtonColor: '#1976d2'
                        });
                    }
                    continue;
                }
                
                // Sort subtitles by start time to ensure proper order
                updateProgress(`Sorting and organizing ${allSubtitles.length} subtitles...`);
                allSubtitles.sort((a, b) => timeToMs(a.startTime) - timeToMs(b.startTime));
                console.log(`Sorted subtitles - First: ${allSubtitles[0].startTime}, Last: ${allSubtitles[allSubtitles.length - 1].startTime}`);
                
                // Renumber subtitles sequentially
                allSubtitles.forEach((subtitle, index) => {
                    subtitle.number = index + 1;
                });
                
                // Generate merged SRT content
                updateProgress(`Generating SRT file for ${language}...`);
                const mergedSrtContent = generateMergedSrt(allSubtitles);
                console.log(`Generated SRT content length: ${mergedSrtContent.length} characters`);
                console.log(`SRT preview (first 500 chars):\n${mergedSrtContent.substring(0, 500)}`);
                
                // Download merged SRT file with simplified approach
                const srtBlob = new Blob([mergedSrtContent], { type: 'text/plain; charset=utf-8' });
                const srtUrl = URL.createObjectURL(srtBlob);
                
                const now = new Date();
                const dateTimeString = now.toISOString()
                    .replace(/[-:]/g, '')
                    .replace(/\.\d{3}Z$/, '')
                    .replace('T', '_');
                
                const cleanFolderName = folderName.replace(/[^a-zA-Z0-9_-]/g, '_');
                const cleanLanguage = language.replace(/[^a-zA-Z0-9_-]/g, '_');
                const filename = `${cleanFolderName}_${cleanLanguage}_merged_${dateTimeString}.srt`;
                
                console.log(`Creating SRT download for: ${filename}`);
                console.log(`SRT Blob size: ${srtBlob.size} bytes`);
                
                // Use same simple approach as JSON download
                const srtLink = document.createElement('a');
                srtLink.href = srtUrl;
                srtLink.download = filename;
                srtLink.style.display = 'none';
                document.body.appendChild(srtLink);
                
                console.log(`Clicking SRT download link for: ${filename}`);
                srtLink.click();
                
                // Clean up immediately like JSON download
                setTimeout(() => {
                    document.body.removeChild(srtLink);
                    URL.revokeObjectURL(srtUrl);
                }, 10000);
                
                updateProgress(`✅ Downloaded ${language} SRT file (${allSubtitles.length} subtitles)`);
                console.log(`✅ SRT download triggered for ${language} with ${allSubtitles.length} subtitles`);
                
                console.log(`✅ Successfully generated merged SRT for ${language} with ${allSubtitles.length} subtitles`);
                successfulMerges++;
                
                // Show progress if multiple languages
                if (selectedLanguages.length > 1) {
                    console.log(`Progress: ${successfulMerges}/${selectedLanguages.length} languages completed`);
                }
            }
            
            console.log(`\n=== Merge Process Summary ===`);
            console.log(`Successful merges: ${successfulMerges}/${selectedLanguages.length}`);
            console.log(`Total errors: ${totalErrors}`);
            
            if (successfulMerges === 0) {
                await Swal.fire({
                    icon: 'error',
                    title: 'No SRT Files Generated',
                    html: `
                        <strong>Possible issues:</strong><br><br>
                        • VTT files are empty or invalid<br>
                        • Network errors fetching VTT files<br>
                        • No languages selected<br>
                        • Check browser console for detailed errors<br><br>
                        <strong>💡 Tip:</strong> Check your browser's Downloads folder and look for any blocked downloads.
                    `,
                    confirmButtonColor: '#1976d2',
                    width: '500px'
                });
                setProcessing(false);
                return;
            }
            
            // Also save the configuration file
            const configuration = {
                folderName,
                videoOrder: videoOrder.map(video => ({
                    id: video.id,
                    videoName: video.videoName,
                    msDuration: video.msDuration,
                    originalIndex: video.originalIndex
                })),
                startOffset,
                videoOffsets,
                selectedLanguages,
                totalDuration,
                timeline: timeline.map(item => ({
                    name: item.name,
                    startTime: item.startTime,
                    endTime: item.endTime,
                    offset: item.offset
                })),
                mergedAt: new Date().toISOString()
            };
            
            // Download configuration file
            const configJsonData = JSON.stringify(configuration, null, 2);
            const configBlob = new Blob([configJsonData], { type: 'application/json' });
            const configUrl = URL.createObjectURL(configBlob);
            
            const now = new Date();
            const dateTimeString = now.toISOString()
                .replace(/[-:]/g, '')
                .replace(/\.\d{3}Z$/, '')
                .replace('T', '_');
            
            const cleanFolderName = folderName.replace(/[^a-zA-Z0-9_-]/g, '_');
            const configFilename = `${cleanFolderName}_merge_config_${dateTimeString}.json`;
            
            try {
                const configLink = document.createElement('a');
                configLink.href = configUrl;
                configLink.download = configFilename;
                configLink.style.display = 'none';
                document.body.appendChild(configLink);
                
                console.log(`Downloading configuration: ${configFilename}`);
                configLink.click();
                
                setTimeout(() => {
                    document.body.removeChild(configLink);
                    URL.revokeObjectURL(configUrl);
                }, 1000);
                
            } catch (configError) {
                console.error('Config download failed:', configError);
                URL.revokeObjectURL(configUrl);
            }
            
            console.log('✅ Merge process completed successfully');
            
            
            
        } catch (error) {
            console.error('Error during merge process:', error);
            Swal.close();
            await Swal.fire({
                icon: 'error',
                title: 'Merge Process Error',
                text: error.message,
                confirmButtonColor: '#f44336'
            });
        } finally {
            setProcessing(false);
        }
    };

    // Delete a video from the order
    const handleDeleteVideo = (videoId) => {
        setVideoOrder(prev => prev.filter(video => video.id !== videoId));
        
        // Clean up related state
        setVideoOffsets(prev => {
            const newOffsets = { ...prev };
            delete newOffsets[videoId];
            return newOffsets;
        });
        
        setVideoOffsetInputs(prev => {
            const newInputs = { ...prev };
            delete newInputs[videoId];
            return newInputs;
        });
        
        setVideoOffsetErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[videoId];
            return newErrors;
        });
    };

    // Reset to default configuration
    const handleReset = () => {
        if (videoFiles.length > 0) {
            // Filter out videos with zero duration
            const validVideos = videoFiles.filter(video => {
                const duration = parseDurationToMs(video.duration);
                return duration > 0;
            });
            
            const resetOrder = validVideos.map((video, index) => ({
                id: video.data || video._id || `video_${index}`,
                msDuration: parseDurationToMs(video.duration),
                originalIndex: index,
                ...video
            }));
            setVideoOrder(resetOrder);
            setStartOffset(0);
            const resetOffsets = {};
            resetOrder.forEach(video => {
                resetOffsets[video.id] = 0;
            });
            setVideoOffsets(resetOffsets);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={!processing ? onClose : undefined}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '85vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MergeIcon color="primary" />
                    <Box>
                        <Typography variant="h6">
                            Merge SRT Files
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {folderName} • {videoOrder.length} videos • Total: {formatTimeWithMs(totalDuration)}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 1 }}>
                {/* Video List with Drag & Drop */}
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Video Order</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total: {formatTimeWithMs(totalDuration)}
                            </Typography>
                            <Tooltip title="Reset to original order">
                                <IconButton onClick={handleReset} size="small">
                                    <ResetIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    
                    {/* Language Selection */}
                    {availableLanguages.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Languages to Merge:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {availableLanguages.map(language => (
                                    <FormControlLabel
                                        key={language}
                                        control={
                                            <Checkbox
                                                checked={selectedLanguages.includes(language)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedLanguages(prev => [...prev, language]);
                                                    } else {
                                                        setSelectedLanguages(prev => prev.filter(lang => lang !== language));
                                                    }
                                                }}
                                                size="small"
                                            />
                                        }
                                        label={language}
                                        sx={{ mr: 2 }}
                                    />
                                ))}
                            </Box>
                            {selectedLanguages.length === 0 && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    Please select at least one language
                                </Typography>
                            )}
                        </Box>
                    )}
                    

                    {/* Start Offset */}
                    <Box sx={{ mb: 1 }}>
                        <TextField
                            label="Start Offset"
                            size="small"
                            value={startOffsetInput}
                            onChange={(e) => handleSmartStartOffsetChange(e.target.value)}
                            onBlur={(e) => handleStartOffsetBlur(e.target.value)}
                            placeholder="00:00:00.000 or type seconds like 5"
                            helperText="Type: 5 (5 sec), 1:30 (1m 30s), or full format"
                            error={!!startOffsetError}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><TimeIcon fontSize="small" /></InputAdornment>,
                            }}
                            sx={{ flex: 1 }}
                        />
                        {startOffsetError && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {startOffsetError}
                            </Typography>
                        )}
                    </Box>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="video-list">
                            {(provided) => (
                                <List
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    sx={{ maxHeight: 600, overflow: 'auto' }}
                                >
                                    {videoOrder.map((video, index) => (
                                        <Draggable key={video.id} draggableId={video.id} index={index}>
                                            {(provided, snapshot) => (
                                                <ListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    sx={{
                                                        mb: 0.5,
                                                        p: 0,
                                                        bgcolor: snapshot.isDragging ? 'action.hover' : 'transparent',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Card variant="outlined" sx={{ width: '100%', '&:hover': { bgcolor: 'action.hover' } }}>
                                                        <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box
                                                                    {...provided.dragHandleProps}
                                                                    sx={{ 
                                                                        cursor: 'grab',
                                                                        '&:active': { cursor: 'grabbing' },
                                                                        color: 'action.active'
                                                                    }}
                                                                >
                                                                    <DragIcon fontSize="small" />
                                                                </Box>
                                                                <Box sx={{ 
                                                                    minWidth: 24, 
                                                                    height: 24, 
                                                                    borderRadius: '50%', 
                                                                    bgcolor: 'primary.main', 
                                                                    color: 'white', 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'center',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    {index + 1}
                                                                </Box>
                                                                {videoOrder.length > 1 && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteVideo(video.id)}
                                                                        sx={{ 
                                                                            color: 'error.main',
                                                                            '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }
                                                                        }}
                                                                        title={`Delete ${video.videoName}`}
                                                                    >
                                                                        <RemoveIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                                {index > 0 && (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
                                                                        <TextField
                                                                            placeholder="5 (sec) or 00:00:05.000"
                                                                            size="small"
                                                                            value={videoOffsetInputs[video.id] || '0'}
                                                                            onChange={(e) => handleSmartOffsetChange(video.id, e.target.value)}
                                                                            onBlur={(e) => handleVideoOffsetBlur(video.id, e.target.value)}
                                                                            error={!!videoOffsetErrors[video.id]}
                                                                            sx={{ 
                                                                                width: 200,
                                                                                '& .MuiInputBase-input': {
                                                                                    fontSize: '0.75rem'
                                                                                }
                                                                            }}
                                                                            InputProps={{
                                                                                startAdornment: (
                                                                                    <InputAdornment position="start">
                                                                                        <TimeIcon fontSize="small" />
                                                                                    </InputAdornment>
                                                                                ),
                                                                            }}
                                                                        />
                                                                        {videoOffsetErrors[video.id] && (
                                                                            <Typography 
                                                                                variant="caption" 
                                                                                color="error" 
                                                                                sx={{ 
                                                                                    position: 'absolute',
                                                                                    top: '100%',
                                                                                    left: 0,
                                                                                    fontSize: '0.65rem',
                                                                                    mt: 0.25,
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                            >
                                                                                {videoOffsetErrors[video.id]}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                            <Typography variant="body2" fontWeight="medium" noWrap sx={{ flex: 1 }}>
                                                                                {video.videoName}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Language chips */}
                                                                        {video.languages && video.languages.length > 0 && (
                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                                                                {video.languages
                                                                                    .filter(lang => lang.vttFileUrl)
                                                                                    .slice(0, 3)
                                                                                    .map((lang, langIndex) => (
                                                                                        <Chip
                                                                                            key={langIndex}
                                                                                            label={lang.language || lang.name || 'Unknown'}
                                                                                            size="small"
                                                                                            variant={selectedLanguages.includes(lang.language || lang.name || 'Unknown') ? "filled" : "outlined"}
                                                                                            color={selectedLanguages.includes(lang.language || lang.name || 'Unknown') ? "primary" : "default"}
                                                                                            sx={{ 
                                                                                                fontSize: '0.65rem',
                                                                                                height: '18px',
                                                                                                '& .MuiChip-label': {
                                                                                                    px: 0.5
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    ))
                                                                                }
                                                                                {video.languages.filter(lang => lang.vttFileUrl).length > 3 && (
                                                                                    <Tooltip
                                                                                        title={
                                                                                            <Box>
                                                                                                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                                                                                                    All Languages:
                                                                                                </Typography>
                                                                                                {video.languages
                                                                                                    .filter(lang => lang.vttFileUrl)
                                                                                                    .map((lang, langIndex) => (
                                                                                                        <Typography key={langIndex} variant="caption" sx={{ display: 'block' }}>
                                                                                                            • {lang.language || lang.name || 'Unknown'}
                                                                                                        </Typography>
                                                                                                    ))
                                                                                                }
                                                                                            </Box>
                                                                                        }
                                                                                        arrow
                                                                                        placement="top"
                                                                                    >
                                                                                        <Chip
                                                                                            label={`+${video.languages.filter(lang => lang.vttFileUrl).length - 3} more`}
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            sx={{ 
                                                                                                fontSize: '0.6rem',
                                                                                                height: '18px',
                                                                                                '& .MuiChip-label': {
                                                                                                    px: 0.5
                                                                                                },
                                                                                                cursor: 'help'
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                )}
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                                            {timeline[index] && `Start: ${formatTimeWithMs(timeline[index].startTime)} • `}{formatDuration(video.msDuration)}
                                                                        </Typography>
                                                                    </Box>
                                                                    {timeline[index] && (
                                                                        <Box sx={{ textAlign: 'right', pl: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                                                                                End: {formatTimeWithMs(timeline[index].endTime)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </ListItem>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Paper>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Input
                    type="file"
                    accept=".json"
                    onChange={handleLoadConfiguration}
                    ref={fileInputRef}
                    sx={{ display: 'none' }}
                />
                
                <Button onClick={onClose} disabled={processing}>
                    Cancel
                </Button>
                
                <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={handleLoadClick}
                    disabled={processing}
                    sx={{ mr: 1 }}
                >
                    Load Config
                </Button>
                
                <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveConfiguration}
                    disabled={processing}
                    sx={{ mr: 1 }}
                >
                    Save Config
                </Button>
                
                <Button
                    variant="contained"
                    startIcon={<MergeIcon />}
                    onClick={handleMerge}
                    disabled={videoOrder.length < 2 || selectedLanguages.length === 0 || processing}
                >
                    {processing ? 'Merging...' : `Merge ${selectedLanguages.length} Language(s) • ${videoOrder.length} Videos`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MergeSrtDialog;